import { useEffect, useState, useMemo } from "react";
import { errorLogAPI } from "../services/api";
import DataTable from "../components/DataTable";
import AdvancedSearchBar from "../components/AdvancedSearchBar";
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";

export default function ErrorLogs() {
  const [errorLogs, setErrorLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [stats, setStats] = useState({
    totalErrors: 0,
    errorsToday: 0,
    errorsThisWeek: 0,
    topErrorMessages: [],
  });

  useEffect(() => {
    loadErrorLogs();
    loadStats();
  }, [page]);

  const loadErrorLogs = async () => {
    try {
      setLoading(true);
      const data = await errorLogAPI.getAll(page, pageSize);
      setErrorLogs(data.data || []);
    } catch (error) {
      toast.error("Failed to load error logs");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await errorLogAPI.getStats();
      setStats(data);
    } catch (error) {
      console.error("Failed to load error stats");
    }
  };

  // Filter error logs based on search query
  const filteredErrorLogs = useMemo(() => {
    if (!searchQuery.trim()) return errorLogs;
    
    const query = searchQuery.toLowerCase();
    return errorLogs.filter(
      (log) =>
        log.error_message?.toLowerCase().includes(query) ||
        log.licenseId?.toString().includes(query) ||
        log.stack_trace?.toLowerCase().includes(query)
    );
  }, [errorLogs, searchQuery]);

  const columns = [
    {
      key: "createdAt",
      label: "Date",
      path: "createdAt",
    },
    {
      key: "licenseId",
      label: "License ID",
      path: "licenseId",
    },
    {
      key: "error_message",
      label: "Error Message",
      path: "error_message",
    },
    {
      key: "stack_trace",
      label: "Stack Trace",
      path: "stack_trace",
    },
    {
      key: "audio_url",
      label: "Audio URL",
      path: "audio_url",
    },
  ];

  const customRenderers = {
    error_message: (value) => (
      <div className="max-w-xs">
        <div className="text-sm text-red-600 font-medium truncate" title={value}>
          {value || "—"}
        </div>
      </div>
    ),
    stack_trace: (value) => (
      <div className="max-w-xs">
        <div className="text-xs text-gray-500 font-mono truncate" title={value}>
          {value ? `${value.substring(0, 50)}...` : "—"}
        </div>
      </div>
    ),
    audio_url: (value) => {
      if (!value) return <span className="text-sm text-gray-400">—</span>;
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 text-sm underline"
        >
          View Audio
        </a>
      );
    },
    licenseId: (value) => (
      <span className="text-sm font-mono text-gray-900">{value || "—"}</span>
    ),
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Error Logs</h1>
          <p className="text-slate-600 text-sm mt-1">
            Monitor and track application errors
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <ErrorIcon className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.totalErrors}</div>
              <div className="text-sm text-gray-500">Total Errors</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <WarningIcon className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.errorsToday}</div>
              <div className="text-sm text-gray-500">Today</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <InfoIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.errorsThisWeek}</div>
              <div className="text-sm text-gray-500">This Week</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <ErrorIcon className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {stats.topErrorMessages.length > 0 ? stats.topErrorMessages[0].count : 0}
              </div>
              <div className="text-sm text-gray-500">Top Error Count</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="space-y-4">
        <AdvancedSearchBar
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          onSearch={(term) => {
            setSearchQuery(term);
            setPage(1);
          }}
          placeholder="Search by error message, license ID, or stack trace..."
          enableDebounce={true}
          debounceDelay={500}
          isLoading={loading}
        />
      </div>

      {/* Top Error Messages */}
      {stats.topErrorMessages.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Error Messages</h3>
          <div className="space-y-2">
            {stats.topErrorMessages.slice(0, 5).map((error, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                <div className="flex-1 text-sm text-gray-700 truncate pr-4" title={error.message}>
                  {error.message}
                </div>
                <div className="text-sm font-medium text-gray-900">
                  {error.count} occurrences
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data Table */}
      <DataTable
        data={filteredErrorLogs}
        columns={columns}
        isLoading={loading}
        searchQuery={searchQuery}
        page={page}
        pageSize={pageSize}
        totalCount={filteredErrorLogs.length}
        onPageChange={setPage}
        itemName="error log"
        fieldMappings={{
          dateFields: ["createdAt"],
        }}
        customRenderers={customRenderers}
      />
    </div>
  );
}
